import React, { useEffect, useRef, cloneElement } from 'react';

let hapticIdCounter = 0;

// Wrap any button/clickable element to add iOS's native haptic tick on tap.
// The wrapped element becomes purely visual - its own onClick (if any) will never
// fire once wrapped, since the switch overlay sits on top and takes the tap instead.
// Move the action into the `onTap` prop:
//
//   <button onClick={fn} className="x">Label</button>
//   becomes
//   <Haptic onTap={fn}><button className="x">Label</button></Haptic>
//
// `disabled` and `type="submit"` on the wrapped child are auto-detected and handled:
// - disabled: the overlay's own input is also disabled, so it genuinely can't be
//   tapped (native browser behavior for disabled form controls), matching a real
//   disabled button instead of silently staying interactive underneath.
// - type="submit": since the real button never receives a native click anymore,
//   this calls form.requestSubmit() on the enclosing <form> itself after onTap.
//
// Exploits WebKit's haptic feedback for <input type="checkbox" switch> (iOS 18+):
// overlays a real, invisible switch input on top of the children so the user's
// actual finger-tap lands on the native control. As of iOS 26.5 this only fires for
// a genuine physical tap - it cannot be triggered from script - so the overlay must
// be the thing the user actually taps, not just present anywhere on the page.
// No-op elsewhere (Android, desktop): onTap still fires via the input's change event,
// just without any haptic.
//
// Deliberately only used on small, precise tap targets (e.g. the bottom nav icons) -
// wrapping a large scrollable card caused the overlay to swallow scroll gestures as
// taps and silently ate the haptic itself. Don't apply this to anything a scroll
// gesture might realistically start on.
//
// Pass `inline` for a text-flow link/button (e.g. inside a paragraph) instead of a
// full-width block button - otherwise the wrapper's width:100% breaks inline flow.
// For anything else non-default (e.g. flex:1 to share a row equally with a sibling),
// pass `wrapperStyle` to merge additional styles onto the wrapper div directly rather
// than growing this component's prop list for every possible layout.
export default function Haptic({ children, onTap, inline = false, wrapperStyle }) {
  const inputRef = useRef(null);
  const id = useRef(`haptic-switch-${++hapticIdCounter}`).current;
  const disabled = Boolean(children.props.disabled);
  const isSubmit = children.props.type === 'submit';

  useEffect(() => {
    // "switch" is a non-standard WebKit attribute React doesn't recognize as a prop.
    inputRef.current?.setAttribute('switch', '');
  }, []);

  const handleChange = () => {
    onTap?.();
    if (isSubmit) inputRef.current?.form?.requestSubmit();
  };

  // pointer-events:none only blocks mouse/touch, not keyboard activation - but since
  // onClick moved to onTap, a plain wrapped <button> has nothing left listening for
  // the native click a keyboard Enter/Space still triggers. Add that fallback here,
  // unless the child already manages its own keyboard handling via onKeyDown/onKeyPress
  // (skip to avoid double-firing the action on Enter/Space). preventDefault also stops
  // a wrapped <a>/Link from following its href natively on Enter, which would otherwise
  // double-navigate alongside onTap.
  const hasOwnKeyHandler = children.props.onKeyDown || children.props.onKeyPress;
  const handleKeyDown = hasOwnKeyHandler ? undefined : (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled) onTap?.();
    }
  };

  // Preserve the child's own tabIndex (if any) so keyboard-Tab access still works.
  const decorativeChild = cloneElement(children, {
    style: { ...(children.props.style || {}), pointerEvents: 'none' },
    ...(handleKeyDown ? { onKeyDown: handleKeyDown } : {}),
  });

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: inline ? 'auto' : '100%', ...wrapperStyle }}>
      {decorativeChild}
      <label
        htmlFor={id}
        style={{ position: 'absolute', inset: 0, opacity: 0, margin: 0, cursor: disabled ? 'default' : 'pointer' }}
      >
        <input
          ref={inputRef}
          type="checkbox"
          id={id}
          tabIndex={-1}
          disabled={disabled}
          onChange={handleChange}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, margin: 0 }}
        />
      </label>
    </div>
  );
}
